import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { api } from '../../api';
import { useSession } from '../../session';
import { ErrorBox, Field, Loading, Stat, inr, today } from '../../ui';
import { download } from './RelationshipParts';

interface Row { key: string; label: string; amountPaise: string; count: number; litres: string; manualCount: number }

const daysAgo = (n: number) => new Date(Date.now() + 330 * 60_000 - n * 86400_000).toISOString().slice(0, 10);

export function Reports() {
  const { can } = useSession();
  const supplier = can('supplier.ledger.view');
  const [from, setFrom] = useState(daysAgo(30));
  const [to, setTo] = useState(today());
  const [groupBy, setGroupBy] = useState(supplier ? 'buyer' : 'vehicle');
  const spend = useQuery({ queryKey: ['spend', from, to, groupBy], queryFn: () => api.get<Row[]>(`/api/reports/spend?from=${from}&to=${to}&groupBy=${groupBy}`) });
  const pilot = useQuery({ queryKey: ['pilot', from, to], queryFn: () => api.get<Record<string, unknown>>(`/api/reports/pilot?from=${from}&to=${to}`) });
  const total = spend.data?.reduce((s, r) => s + BigInt(r.amountPaise), 0n) ?? 0n;
  const p = pilot.data as Record<string, string | number | null> | undefined;
  return (
    <div className="stack">
      <div className="card">
        <h1>Reports</h1>
        <div className="grid three">
          <Field label="From"><input type="date" value={from} onChange={(e) => setFrom(e.target.value)} /></Field>
          <Field label="To"><input type="date" value={to} onChange={(e) => setTo(e.target.value)} /></Field>
          <Field label="Group by">
            <select value={groupBy} onChange={(e) => setGroupBy(e.target.value)}>
              {supplier ? <option value="buyer">Customer</option> : <option value="supplier">Supplier</option>}
              <option value="vehicle">Vehicle</option>
              <option value="driver">Driver</option>
              <option value="fuelType">Fuel</option>
              <option value="location">Pump</option>
              {!supplier ? <option value="costCentre">Project / cost centre</option> : null}
              <option value="day">Day</option>
            </select>
          </Field>
        </div>
        <div className="row">
          <button className="btn small" onClick={() => void download(`/api/reports/spend?from=${from}&to=${to}&groupBy=${groupBy}&format=csv`, `spend-${groupBy}-${from}-${to}.csv`)}>Download this table (CSV)</button>
          <button className="btn small" onClick={() => void download(`/api/reports/transactions.csv?from=${from}&to=${to}`, `transactions-${from}-${to}.csv`)}>All transactions for accounting (CSV)</button>
          <button className="btn small" onClick={() => void download(`/api/reports/pilot?from=${from}&to=${to}&format=csv`, `pilot-${from}-${to}.csv`)}>Pilot numbers (CSV)</button>
        </div>
      </div>
      <div className="card">
        <h2>Spending</h2>
        {spend.isLoading ? <Loading /> : <ErrorBox error={spend.error} />}
        <div className="table-wrap">
          <table>
            <thead><tr><th>{groupBy}</th><th className="num">Supplies</th><th className="num">Litres</th><th className="num">Amount</th></tr></thead>
            <tbody>
              {spend.data?.map((r) => <tr key={r.key}><td>{r.label}{r.manualCount ? <small className="muted"> ({r.manualCount} by hand)</small> : null}</td><td className="num">{r.count}</td><td className="num">{r.litres}</td><td className="num">{inr(r.amountPaise)}</td></tr>)}
              <tr><td><strong>Total</strong></td><td /><td /><td className="num"><strong>{inr(total.toString())}</strong></td></tr>
            </tbody>
          </table>
        </div>
      </div>
      <div className="card">
        <h2>Pilot measurements</h2>
        <p className="muted">Counted from real records only. Fuel efficiency (km per litre) is not shown until enough reliable odometer readings exist.</p>
        {pilot.isLoading ? <Loading /> : p ? (
          <div className="grid three">
            <Stat label="Indents created" value={p.indentsCreated} />
            <Stat label="Asked by drivers" value={p.indentsRequestedByDrivers} />
            <Stat label="Approved" value={p.indentsApproved} />
            <Stat label="Typical approval time" value={p.medianApprovalMinutes === null ? '—' : `${p.medianApprovalMinutes} min`} />
            <Stat label="Digital supplies" value={p.suppliesDigital} />
            <Stat label="Recorded by hand" value={p.suppliesManual} />
            <Stat label="Actual vs approved" value={p.actualToAuthorisedPercent === null ? '—' : `${p.actualToAuthorisedPercent}%`} />
            <Stat label="Waiting driver confirmation" value={p.pendingAcknowledgements} />
            <Stat label="Disputes (open)" value={`${p.disputesRaised} (${p.disputesOpen})`} />
            <Stat label="Overdue now" value={inr(String(p.overduePaise))} />
          </div>
        ) : null}
      </div>
    </div>
  );
}
